import { ChangeDetectorRef, Component, Inject, TemplateRef, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef, MatSnackBar, MatSnackBarConfig } from '@angular/material';
import { EditAuditItemDialogComponent } from '../edit-audit-item-dialog/edit-audit-item-dialog.component';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { formstate } from '../../../reducers';

@Component({
  selector: 'vr-preview-audit-dialog',
  templateUrl: './preview-audit-dialog.component.html',
  styleUrls: ['./preview-audit-dialog.component.scss']
})
export class PreviewAuditDialogComponent {
  audit: any;
  stats: any[];
  old: any[];
  file_url: string;
  frameEvalOptions: any[];
  hasChanges = false;
  saving = false;
  @ViewChild('confirmCloseTemplate') confirmCloseTemplate: TemplateRef<any>;
  confirmDialogRef: any;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<PreviewAuditDialogComponent>,
    private dialog: MatDialog,
    private changeDetectorRefs: ChangeDetectorRef,
    private http: HttpClient,
    private snackBar: MatSnackBar
  ) {
    this.audit = data.audit;
    this.stats = data.stats;
    this.old = data.old || [];
    this.file_url = data.file_url;
    this.frameEvalOptions = data.frame_evaluation_options || [];
    // Le backend renvoie parfois coefficient/malus/ko/evaluation/categories/information sous forme
    // de chaîne JSON non parsée : on normalise avant tout calcul pour éviter les NaN silencieux.
    this.normalizeAudit(this.audit);
    this.old.forEach((oldAudit: any) => this.normalizeAudit(oldAudit));
    this.recalculateStats();
  }

  private safeParseJSON(value: any, fallback: any): any {
    if (value === null || value === undefined) { return fallback; }
    if (typeof value !== 'string') { return value; }
    try {
      const parsed = JSON.parse(value);
      return parsed === null || parsed === undefined ? fallback : parsed;
    } catch (e) {
      return fallback;
    }
  }

  private normalizeAudit(audit: any) {
    if (!audit) { return; }
    audit.coefficient = this.safeParseJSON(audit.coefficient, {});
    audit.malus = this.safeParseJSON(audit.malus, {});
    audit.ko = this.safeParseJSON(audit.ko, {});
    (audit.audit_item_groups || []).forEach((group: any) => {
      (group.audit_items || []).forEach((item: any) => {
        item.evaluation = this.safeParseJSON(item.evaluation, {});
        item.categories = this.safeParseJSON(item.categories, []);
        item.information = this.safeParseJSON(item.information, []);
      });
    });
  }

  getNotePercent(values: any, max: any, index: number): number {
    if (!max[index] || max[index] === 0) return 0;
    const pct = (values[index] / max[index]) * 100;
    return pct < 0 ? 0 : pct;
  }

  getNoteColor(pct: number): string {
    if (pct >= 85) return 'note-green';
    if (pct >= 60) return 'note-orange';
    return 'note-red';
  }

  close() {
    if (this.hasChanges) {
      this.confirmDialogRef = this.dialog.open(this.confirmCloseTemplate, { width: '520px', disableClose: true });
    } else {
      this.dialogRef.close();
    }
  }

  forceClose() {
    if (this.confirmDialogRef) { this.confirmDialogRef.close(); }
    this.dialogRef.close();
  }

  saveAndClose() {
    this.saving = true;
    this.http.put<any>(environment.API_PATH + 'audit', { audit: this.audit }, { withCredentials: true }).subscribe(
      response => {
        this.saving = false;
        if (response['success']) {
          formstate.setverif(false);
          if (this.confirmDialogRef) { this.confirmDialogRef.close(); }
          this.dialogRef.close();
        } else {
          this.snackBar.open('Une erreur est survenue, veuillez réessayer.', '', { duration: 5000 } as MatSnackBarConfig);
        }
      },
      () => {
        this.saving = false;
        this.snackBar.open('Une erreur est survenue, veuillez réessayer.', '', { duration: 5000 } as MatSnackBarConfig);
      }
    );
  }

  parseDate(dateStr: string): Date | null {
    if (!dateStr) return null;
    return new Date(dateStr.replace(' ', 'T'));
  }

  getGroupLetter(index: number): string {
    return String.fromCharCode(65 + index);
  }

  getOldItemEvaluation(item: any, oldIndex: number): string {
    if (!this.old || !this.old[oldIndex] || !this.old[oldIndex].items) return '';
    const found = this.old[oldIndex].items.find((i: any) => i.detail === item.detail);
    return found ? (found.response_evaluation || '') : '';
  }

  editItem(item: any, gIdx: number, iIdx: number) {
    this.dialog.open(EditAuditItemDialogComponent, {
      height: '90vh',
      width: '90vw',
      maxWidth: '90vw',
      data: { item: item, audit_id: this.audit.id, old: this.old, evalOptions: this.frameEvalOptions }
    } as MatDialogConfig).afterClosed().subscribe((result) => {
      if (result && result['saved']) {
        this.audit.audit_item_groups[gIdx].audit_items[iIdx] = result['item'];
        this.hasChanges = true;
        this.recalculateStats();
        this.changeDetectorRefs.detectChanges();
      }
    });
  }

  saveAudit() {
    this.saving = true;
    this.http.put<any>(environment.API_PATH + 'audit', { audit: this.audit }, { withCredentials: true }).subscribe(
      response => {
        this.saving = false;
        if (response['success']) {
          this.hasChanges = false;
          formstate.setverif(false);
          this.snackBar.open('Audit sauvegardé avec succès.', '', { duration: 3000 } as MatSnackBarConfig);
        } else {
          this.snackBar.open('Une erreur est survenue, veuillez réessayer.', '', { duration: 5000 } as MatSnackBarConfig);
        }
      },
      () => {
        this.saving = false;
        this.snackBar.open('Une erreur est survenue, veuillez réessayer.', '', { duration: 5000 } as MatSnackBarConfig);
      }
    );
  }

  recalculateStats() {
    // Réinitialiser les valeurs de l'audit courant (index 0)
    this.stats.forEach(stat => {
      stat.values[0] = 0;
      stat.max[0] = 0;
    });

    // Recalculer depuis les items
    this.audit.audit_item_groups.forEach((group: any) => {
      group.audit_items.forEach((item: any) => {
        if (Array.isArray(item.categories) && item.response_evaluation !== 'SO' && item.response_evaluation !== 'PO') {
          item.categories.forEach((category: string) => {
            this.addCategoryToStats(category, item);
          });
        }
      });
    });

    // Recalculer la note globale de l'audit en cours (édité en direct dans ce dialog).
    // Les anciens audits sont en lecture seule : on garde global_note tel que fourni par le backend
    // (source de vérité), pour éviter toute divergence avec la formule métier réelle.
    this.audit.global_note = this.calcGlobalNote(this.audit);
  }

  private calcGlobalNote(audit: any): number {
    let totalValues = 0;
    let totalMax = 0;
    const malus = audit.malus || {};
    let totalMalusPercent = 0;
    let numberOfKO = 0;
    (audit.audit_item_groups || []).forEach((group: any) => {
      (group.audit_items || []).forEach((item: any) => {
        const coeff = (audit.coefficient && item.item_type && audit.coefficient[item.item_type]) ? audit.coefficient[item.item_type] : 1;
        if (item.response_evaluation !== 'SO' && item.response_evaluation !== 'PO') {
          let maxItem = 0;
          for (const key in item.evaluation) { if (item.evaluation[key] > maxItem) { maxItem = item.evaluation[key]; } }
          totalMax += coeff * maxItem;
          if (item.response_evaluation === 'NC') {
            const ncKey = ['NCMIN', 'NCMOD', 'NCMAJ', 'NCCRI'].find((k: string) => item.evaluation[k] !== undefined);
            if (ncKey) { totalValues += coeff * item.evaluation[ncKey]; }
          } else if (item.response_evaluation) {
            totalValues += coeff * (item.evaluation[item.response_evaluation] || 0);
          }
          const evalKey = item.response_evaluation === 'NC' ? 'NCMIN' : item.response_evaluation;
          totalMalusPercent += malus[evalKey] || 0;
        }
        if (this.isKoEval(item.response_evaluation)) { numberOfKO++; }
      });
    });
    const noteMax = audit.noteMax || 100;
    const rawNote = totalMax > 0 ? Math.round((totalValues / totalMax) * noteMax * 10) / 10 : 0;
    let noteAfterMalus = rawNote * (1 - totalMalusPercent / 100);

    const ko = audit.ko || {};
    const koOrder = ['un', 'deux', 'trois', 'quatre'];
    const availableKo = koOrder.filter(k => ko[k] !== undefined && ko[k] !== null);
    if (numberOfKO > 0 && availableKo.length > 0) {
      const idx = Math.min(numberOfKO, availableKo.length) - 1;
      const malusKO = ko[availableKo[idx]] || 0;
      noteAfterMalus = malusKO >= 100 ? 0 : Math.round(noteAfterMalus * (1 - malusKO / 100) * 10) / 10;
    }

    return Math.min(Math.max(noteAfterMalus, 0), noteMax);
  }

  private addCategoryToStats(category: string, item: any) {
    const coefficient = (item.item_type && this.audit.coefficient && this.audit.coefficient[item.item_type])
      ? this.audit.coefficient[item.item_type] : 1;

    let found = this.stats.find(stat => stat.category === category);
    if (!found) {
      found = { category, values: { 0: 0, 1: 0, 2: 0, 3: 0 }, max: { 0: 0, 1: 0, 2: 0, 3: 0 } };
      this.stats.push(found);
    }

    let max = 0;
    for (const key in item.evaluation) {
      if (item.evaluation[key] > max) {
        max = item.evaluation[key];
      }
    }

    found.max[0] += coefficient * max;

    if (item.response_evaluation === 'NC') {
      if (item.evaluation['NCMIN'] !== undefined)      { found.values[0] += coefficient * item.evaluation['NCMIN']; }
      else if (item.evaluation['NCMOD'] !== undefined) { found.values[0] += coefficient * item.evaluation['NCMOD']; }
      else if (item.evaluation['NCMAJ'] !== undefined) { found.values[0] += coefficient * item.evaluation['NCMAJ']; }
      else if (item.evaluation['NCCRI'] !== undefined) { found.values[0] += coefficient * item.evaluation['NCCRI']; }
    } else if (item.response_evaluation) {
      found.values[0] += coefficient * item.evaluation[item.response_evaluation];
    }

    if (found.values[0] < 0) { found.values[0] = 0; }
  }

  getEvalLabel(code: string): string {
    if (this.frameEvalOptions && this.frameEvalOptions.length > 0) {
      const opt = this.frameEvalOptions.find(o => o.code === code);
      return opt ? opt.code : code;
    }
    const defaults: {[key: string]: string} = {
      'C': 'Conforme', 'CP': 'Part. Conforme', 'PC': 'Part. Conforme',
      'NCMIN': 'NC Mineur', 'NCMOD': 'NC Moyen',
      'NCMAJ': 'NC Majeur', 'NCCRI': 'NC Critique',
      'SO': 'Sans Objet', 'PO': 'Non Observable'
    };
    return defaults[code] || code;
  }

  getEvalStyle(code: string): any {
    if (this.frameEvalOptions && this.frameEvalOptions.length > 0) {
      const opt = this.frameEvalOptions.find(o => o.code === code);
      if (opt) { return { 'background-color': opt.back_color, 'color': opt.fore_color }; }
      return { 'background-color': '#A6A6A6', 'color': '#FFFFFF' };
    }
    const colors: {[key: string]: string} = {
      'C': '#33CC33', 'CP': '#A9D08E', 'NCMIN': '#FFCC00',
      'NCMOD': '#FF7400', 'NCMAJ': '#FF0000', 'NCCRI': '#000000',
    };
    const bg = colors[code] || '#A6A6A6';
    return { 'background-color': bg, 'color': '#FFFFFF' };
  }

  isArray(value: any): boolean {
    return Array.isArray(value);
  }

  isKoEval(code: string): boolean {
    if (!code) { return false; }
    if (this.frameEvalOptions && this.frameEvalOptions.length > 0) {
      const opt = this.frameEvalOptions.find(o => o.code === code);
      return opt ? opt.is_ko : false;
    }
    // Ancien système de notation : la gestion KO (frame.ko.un/deux/trois/quatre) ne concerne
    // que les NC Critique (NCCRI), comme documenté dans l'éditeur de trame (frames-edit.component.html).
    // Compter aussi CP/NCMIN/NCMOD/NCMAJ ici fait déclencher les paliers KO (et donc la note à 0
    // via `malusKO >= 100`) pour de simples non-conformités mineures.
    return code === 'NCCRI';
  }
}



// WEBPACK FOOTER //
// /home/web_clients/qualicheck.fr/preprod/erp/src/app/core/dialogs/preview-audit-dialog/preview-audit-dialog.component.ts